import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { add, format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { green } from "@material-ui/core/colors";
import {
  Button, IconButton, Paper,
  Tooltip, Typography,
  CircularProgress, Box, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem as MuiMenuItem, FormControl, InputLabel, Fab,
} from "@material-ui/core";
import {
  Edit, CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree, DeleteOutline,
  Facebook, Instagram, WhatsApp, Sync, Add, LinkOutlined,
} from "@material-ui/icons";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from "../../utils/formatSerializedId";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";

// Oculta temporalmente WhatsApp y WhatsApp Oficial del menú "Nueva conexión".
// Las conexiones existentes no se tocan. Cambiar a false para volver a mostrarlos.
const HIDE_WHATSAPP_NEW_CONNECTION = true;

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    background: "transparent",
    ...theme.scrollbarStyles,
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0,0,0,0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450,
  },
  tooltipPopper: { textAlign: "center" },
  buttonProgress: { color: green[500] },

  // ── Desktop grid ──────────────────────────────────────
  desktopGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: theme.spacing(2),
  },
  desktopCard: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
  },
  desktopCardColorBar: {
    height: 5,
    width: "100%",
    background: "linear-gradient(90deg, #5B2D8E, #7C3AED)",
  },
  desktopCardBody: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    flex: 1,
  },
  desktopCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  desktopCardIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: "rgba(91,45,142,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { fontSize: "1.5rem" },
  },
  desktopCardInfo: { flex: 1, minWidth: 0 },
  desktopCardName: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  desktopCardNumber: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  desktopCardMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  desktopCardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 2, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  desktopCardActions: {
    display: "flex",
    gap: 4,
  },
  desktopCardDate: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },

  // ── Header stats ──────────────────────────────────────
  statsBar: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statChip: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: "0.8rem",
    fontWeight: 600,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },

  // ── Mobile cards (unchanged) ──────────────────────────
  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  cardIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "rgba(91,45,142,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardNumber: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  cardBottom: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  cardActions: {
    display: "flex",
    gap: 2,
    flexWrap: "wrap",
  },
  colorBar: {
    height: 4,
    borderRadius: 2,
    width: "100%",
  },
  mobileHeader: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
    padding: theme.spacing(0, 0, 1),
  },
  mobileHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  mobileHeaderButtons: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  fab: {
    position: "fixed",
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 10,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box top={0} left={0} bottom={0} right={0} position="absolute"
        display="flex" alignItems="center" justifyContent="center">
        <Typography variant="caption" component="div" color="textSecondary">
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();
  return (
    <Tooltip arrow
      classes={{ tooltip: classes.tooltip, popper: classes.tooltipPopper }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">{title}</Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const IconChannel = (channel) => {
  switch (channel) {
    case "facebook": return <Facebook style={{ color: "#3b5998" }} />;
    case "instagram": return <Instagram style={{ color: "#e1306c" }} />;
    case "whatsapp": return <WhatsApp style={{ color: "#25d366" }} />;
    case "whatsapp_oficial": return <WhatsApp style={{ color: "#25d366" }} />;
    default: return null;
  }
};

const Connections = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [channel, setChannel] = useState("whatsapp");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [planConfig, setPlanConfig] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [sourceConnection, setSourceConnection] = useState("");
  const [targetConnection, setTargetConnection] = useState("");
  const [preDeleteModalOpen, setPreDeleteModalOpen] = useState(false);
  const [whatsAppToDelete, setWhatsAppToDelete] = useState(null);
  const [transferProgressModalOpen, setTransferProgressModalOpen] = useState(false);
  const [transferProgress, setTransferProgress] = useState({ current: 0, total: 0, percentage: 0 });

  const confirmationModalInitialState = { action: "", title: "", message: "", whatsAppId: "", open: false };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);

  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      try {
        const planConfigs = await getPlanCompany(undefined, user.companyId);
        setPlanConfig(planConfigs);
      } catch (err) {}
    }
    fetchData();
  }, []);

  useEffect(() => {
    socket.on(`importMessages-${user.companyId}`, (data) => {
      if (data.action === "refresh") { setStatusImport([]); history.go(0); }
      if (data.action === "update") setStatusImport(data.status);
    });
    socket.on(`transferTickets-${user.companyId}`, (data) => {
      if (data.action === "progress") {
        setTransferProgress({ current: data.current, total: data.total, percentage: Math.round((data.current / data.total) * 100) });
      }
      if (data.action === "completed") {
        setTransferProgressModalOpen(false);
        setTransferProgress({ current: 0, total: 0, percentage: 0 });
        toast.success(`Transferência concluída! ${data.transferred} tickets transferidos.`);
        handleCloseTransferModal();
      }
      if (data.action === "error") {
        setTransferProgressModalOpen(false);
        setTransferProgress({ current: 0, total: 0, percentage: 0 });
        toast.error("Erro na transferência de tickets.");
      }
    });
  }, [whatsApps]);

  const handleStartWhatsAppSession = async (id) => {
    try { await api.post(`/whatsappsession/${id}`); } catch (err) { toastError(err); }
  };
  const handleRequestNewQrCode = async (id) => {
    try { await api.put(`/whatsappsession/${id}`); } catch (err) { toastError(err); }
  };
  const handleOpenWhatsAppModal = (ch) => { setChannel(ch || "whatsapp"); setSelectedWhatsApp(null); setWhatsAppModalOpen(true); };
  const handleCloseWhatsAppModal = useCallback(() => { setWhatsAppModalOpen(false); setSelectedWhatsApp(null); }, []);
  const handleOpenQrModal = (w) => { setSelectedWhatsApp(w); setQrModalOpen(true); };
  const handleCloseQrModal = useCallback(() => { setSelectedWhatsApp(null); setQrModalOpen(false); }, []);
  const handleEditWhatsApp = (w) => { setChannel(w.channel); setSelectedWhatsApp(w); setWhatsAppModalOpen(true); };
  const handleSyncTemplates = async (id) => { await api.get(`/whatsapp/sync-templates/${id}`); };
  const handleCopyWebhook = (url) => { navigator.clipboard.writeText(url); toast.success("Webhook copiado"); };
  const openInNewTab = (url) => window.open(url, "_blank", "noopener,noreferrer");

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    const map = {
      disconnect: { title: i18n.t("connections.confirmationModal.disconnectTitle"), message: i18n.t("connections.confirmationModal.disconnectMessage") },
      delete: { title: i18n.t("connections.confirmationModal.deleteTitle"), message: i18n.t("connections.confirmationModal.deleteMessage") },
      closedImported: { title: i18n.t("connections.confirmationModal.closedImportedTitle"), message: i18n.t("connections.confirmationModal.closedImportedMessage") },
    };
    setConfirmModalInfo({ action, whatsAppId, ...map[action] });
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    try {
      if (confirmModalInfo.action === "disconnect") await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      if (confirmModalInfo.action === "delete") { await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`); toast.success(i18n.t("connections.toasts.deleted")); }
      if (confirmModalInfo.action === "closedImported") { await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`); toast.success(i18n.t("connections.toasts.closedimported")); }
    } catch (err) { toastError(err); }
    setConfirmModalInfo(confirmationModalInitialState);
  };

  const handleOpenTransferModal = () => setTransferModalOpen(true);
  const handleCloseTransferModal = () => { setTransferModalOpen(false); setSourceConnection(""); setTargetConnection(""); };
  const handleCloseTransferProgressModal = () => { setTransferProgressModalOpen(false); setTransferProgress({ current: 0, total: 0, percentage: 0 }); };

  const handleTransferTickets = async () => {
    if (!sourceConnection || !targetConnection) { toast.error("Selecione as conexões de origem e destino"); return; }
    if (sourceConnection === targetConnection) { toast.error("As conexões devem ser diferentes"); return; }
    try {
      const response = await api.post(`/transfer-tickets`, { sourceConnectionId: sourceConnection, targetConnectionId: targetConnection });
      if (response.data.requiresProgress) {
        setTransferModalOpen(false);
        setTransferProgressModalOpen(true);
        setTransferProgress({ current: 0, total: response.data.totalTickets, percentage: 0 });
      } else {
        toast.success(`Tickets transferidos! ${response.data.transferred || 0} transferidos.`);
        handleCloseTransferModal();
      }
    } catch (err) { toastError(err); }
  };

  const handleOpenPreDeleteModal = (id) => { setWhatsAppToDelete(id); setPreDeleteModalOpen(true); };
  const handleClosePreDeleteModal = () => { setPreDeleteModalOpen(false); setWhatsAppToDelete(null); };
  const handleConfirmTransferDone = () => { setPreDeleteModalOpen(false); handleOpenConfirmationModal("delete", whatsAppToDelete); setWhatsAppToDelete(null); };
  const restartWhatsapps = async () => {
    try { await api.post(`/whatsapp-restart/`); toast.success(i18n.t("connections.waitConnection")); } catch (err) { toastError(err); }
  };

  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button size="small" variant="outlined" color="primary"
          onClick={() => handleOpenConfirmationModal("closedImported", whatsApp.id)}>
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }
    if (whatsApp?.importOldMessages) {
      const isTimeStamp = !isNaN(new Date(Math.floor(whatsApp?.statusImportMessages)).getTime());
      if (isTimeStamp) {
        const ultimoStatus = new Date(Math.floor(whatsApp?.statusImportMessages)).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: 35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <Button disabled size="small" variant="outlined" color="primary"
              endIcon={<CircularProgress size={12} className={classes.buttonProgress} />}>
              {i18n.t("connections.buttons.preparing")}
            </Button>
          );
        }
      }
    }
  };

  const renderStatusIcon = (whatsApp) => {
    if (whatsApp.status === "DISCONNECTED") return <SignalCellularConnectedNoInternet0Bar color="secondary" fontSize="small" />;
    if (whatsApp.status === "OPENING") return <CircularProgress size={18} className={classes.buttonProgress} />;
    if (whatsApp.status === "qrcode") return <CropFree fontSize="small" />;
    if (whatsApp.status === "CONNECTED") return <SignalCellular4Bar style={{ color: green[500] }} fontSize="small" />;
    if (whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") return <SignalCellularConnectedNoInternet2Bar color="secondary" fontSize="small" />;
    return null;
  };

  const getStatusLabel = (status) => {
    const map = { CONNECTED: "Conectado", DISCONNECTED: "Desconectado", qrcode: "QR Code", OPENING: "Conectando", TIMEOUT: "Timeout", PAIRING: "Emparejando" };
    return map[status] || status;
  };

  const getStatusStyle = (status) => {
    if (status === "CONNECTED") return { background: "rgba(34,197,94,0.12)", color: "#16a34a" };
    if (status === "DISCONNECTED") return { background: "rgba(239,68,68,0.12)", color: "#dc2626" };
    if (status === "qrcode") return { background: "rgba(245,158,11,0.12)", color: "#d97706" };
    if (status === "OPENING") return { background: "rgba(59,130,246,0.12)", color: "#2563eb" };
    return { background: "rgba(107,114,128,0.12)", color: "#6b7280" };
  };

  const getConnectionNumber = (w) => {
    if (w.channel === "whatsapp" && w.number) return formatSerializedId(w.number);
    if (w.channel === "whatsapp_oficial" && w.phone_number) return formatSerializedId(w.phone_number);
    return w.channel;
  };

  const renderActionButtons = (whatsApp) => (
    <>
      {whatsApp.channel === "whatsapp" && whatsApp.status === "qrcode" && (
        <Can role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
          perform="connections-page:addConnection"
          yes={() => (
            <Button size="small" variant="contained" color="primary" onClick={() => handleOpenQrModal(whatsApp)}>
              {i18n.t("connections.buttons.qrcode")}
            </Button>
          )} />
      )}
      {whatsApp.channel === "whatsapp" && whatsApp.status === "DISCONNECTED" && (
        <Can role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
          perform="connections-page:addConnection"
          yes={() => (
            <>
              <Button size="small" variant="outlined" color="primary" onClick={() => handleStartWhatsAppSession(whatsApp.id)}>
                {i18n.t("connections.buttons.tryAgain")}
              </Button>{" "}
              <Button size="small" variant="outlined" color="secondary" onClick={() => handleRequestNewQrCode(whatsApp.id)}>
                {i18n.t("connections.buttons.newQr")}
              </Button>
            </>
          )} />
      )}
      {whatsApp.channel === "whatsapp" && ["CONNECTED", "PAIRING", "TIMEOUT"].includes(whatsApp.status) && (
        <Can role={user.profile} perform="connections-page:addConnection"
          yes={() => (
            <>
              <Button size="small" variant="outlined" color="secondary"
                onClick={() => handleOpenConfirmationModal("disconnect", whatsApp.id)}>
                {i18n.t("connections.buttons.disconnect")}
              </Button>
              {renderImportButton(whatsApp)}
            </>
          )} />
      )}
      {whatsApp.channel === "whatsapp" && whatsApp.status === "OPENING" && (
        <Button size="small" variant="outlined" disabled color="default">
          {i18n.t("connections.buttons.connecting")}
        </Button>
      )}
    </>
  );

  const newConnectionMenu = (
    <PopupState variant="popover" popupId="new-connection-menu">
      {(popupState) => (
        <React.Fragment>
          <Can role={user.profile} perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button variant="contained" color="primary" {...bindTrigger(popupState)}>
                  {i18n.t("connections.newConnection")}
                </Button>
                <Menu {...bindMenu(popupState)}>
                  {!HIDE_WHATSAPP_NEW_CONNECTION && (
                    <MenuItem disabled={!planConfig?.plan?.useWhatsapp}
                      onClick={() => { handleOpenWhatsAppModal("whatsapp"); popupState.close(); }}>
                      <WhatsApp fontSize="small" style={{ marginRight: 10, color: "#25D366" }} /> WhatsApp
                    </MenuItem>
                  )}
                  {!HIDE_WHATSAPP_NEW_CONNECTION && (
                    <MenuItem disabled={!planConfig?.plan?.useWhatsappOfficial}
                      onClick={() => { handleOpenWhatsAppModal("whatsapp_oficial"); popupState.close(); }}>
                      <WhatsApp fontSize="small" style={{ marginRight: 10, color: "#25D366" }} /> WhatsApp Oficial
                    </MenuItem>
                  )}
                  <FacebookLogin appId={process.env.REACT_APP_FACEBOOK_APP_ID} autoLoad={false}
                    fields="name,email,picture" version="9.0"
                    scope={process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                      ? "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,pages_manage_engagement,pages_read_user_content,business_management"
                      : "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,pages_manage_engagement,pages_read_user_content"}
                    callback={(r) => { if (r.status !== "unknown") { api.post("/facebook", { facebookUserId: r.id, facebookUserToken: r.accessToken }).then(() => toast.success(i18n.t("connections.facebook.success"))).catch(toastError); } }}
                    render={(rp) => (
                      <MenuItem disabled={!planConfig?.plan?.useFacebook} onClick={rp.onClick}>
                        <Facebook fontSize="small" style={{ marginRight: 10, color: "#3b5998" }} /> Facebook
                      </MenuItem>
                    )} />
                  <FacebookLogin appId={process.env.REACT_APP_FACEBOOK_APP_ID} autoLoad={false}
                    fields="name,email,picture" version="9.0"
                    scope={process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                      ? "public_profile,instagram_basic,instagram_manage_messages,instagram_manage_comments,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,pages_manage_engagement,pages_read_user_content,business_management"
                      : "public_profile,instagram_basic,instagram_manage_messages,instagram_manage_comments,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,pages_manage_engagement,pages_read_user_content"}
                    callback={(r) => { if (r.status !== "unknown") { api.post("/facebook", { addInstagram: true, facebookUserId: r.id, facebookUserToken: r.accessToken }).then(() => toast.success(i18n.t("connections.facebook.success"))).catch(toastError); } }}
                    render={(rp) => (
                      <MenuItem disabled={!planConfig?.plan?.useInstagram} onClick={rp.onClick}>
                        <Instagram fontSize="small" style={{ marginRight: 10, color: "#e1306c" }} /> Instagram
                      </MenuItem>
                    )} />
                </Menu>
              </>
            )} />
        </React.Fragment>
      )}
    </PopupState>
  );

  return (
    <MainContainer>
      <ConfirmationModal title={confirmModalInfo.title} open={confirmModalOpen}
        onClose={setConfirmModalOpen} onConfirm={handleSubmitConfirmationModal}>
        {confirmModalInfo.message}
      </ConfirmationModal>
      {qrModalOpen && (
        <QrcodeModal open={qrModalOpen} onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id} />
      )}
      <WhatsAppModal open={whatsAppModalOpen} onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id} channel={channel} />

      {/* Transfer modal */}
      <Dialog open={transferModalOpen} onClose={handleCloseTransferModal} maxWidth="sm" fullWidth>
        <DialogTitle>Transferência de Tickets</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            Selecione a conexão de <strong>origem</strong> e <strong>destino</strong>. Todos os tickets ativos serão movidos.
          </Typography>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <FormControl fullWidth>
              <InputLabel>Origem</InputLabel>
              <Select value={sourceConnection} onChange={(e) => setSourceConnection(e.target.value)}>
                {whatsApps.map((w) => <MuiMenuItem key={w.id} value={w.id}>{w.name}</MuiMenuItem>)}
              </Select>
            </FormControl>
            <Typography style={{ fontWeight: 700, fontSize: 20, color: green[500] }}>→</Typography>
            <FormControl fullWidth>
              <InputLabel>Destino</InputLabel>
              <Select value={targetConnection} onChange={(e) => setTargetConnection(e.target.value)}>
                {whatsApps.map((w) => <MuiMenuItem key={w.id} value={w.id}>{w.name}</MuiMenuItem>)}
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransferModal}>Cancelar</Button>
          <Button onClick={handleTransferTickets} color="primary" variant="contained">Transferir</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer progress modal */}
      <Dialog open={transferProgressModalOpen} onClose={handleCloseTransferProgressModal} maxWidth="sm" fullWidth disableBackdropClick disableEscapeKeyDown>
        <DialogTitle>Transferindo Tickets</DialogTitle>
        <DialogContent>
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <CircularProgressWithLabel value={transferProgress.percentage} size={80} thickness={4} />
            <Typography variant="body1" style={{ marginTop: 16 }}>
              {transferProgress.current} de {transferProgress.total} tickets transferidos
            </Typography>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pre-delete modal */}
      <Dialog open={preDeleteModalOpen} onClose={handleClosePreDeleteModal} maxWidth="sm" fullWidth>
        <DialogTitle>Transferência de Tickets</DialogTitle>
        <DialogContent>
          <Typography>Antes de excluir esta conexão, você já fez a transferência dos tickets para outra conexão?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreDeleteModal}>Não</Button>
          <Button onClick={handleConfirmTransferDone} color="primary" variant="contained">Sim</Button>
        </DialogActions>
      </Dialog>

      {user.profile === "user" && user.allowConnections === "disabled" ? <ForbiddenPage /> : (
        <>
          {isMobile ? (
            <>
              <MainHeader>
                <div className={classes.mobileHeader}>
                  <div className={classes.mobileHeaderRow}>
                    <Title>{i18n.t("connections.title")}</Title>
                    <Typography variant="caption" color="textSecondary">
                      {whatsApps.length} conexiones
                    </Typography>
                  </div>
                  <div className={classes.mobileHeaderButtons}>
                    <Button size="small" variant="outlined" color="primary" onClick={restartWhatsapps}>
                      {i18n.t("connections.restartConnections")}
                    </Button>
                    <Button size="small" variant="outlined" color="primary"
                      onClick={() => openInNewTab(`https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`)}>
                      {i18n.t("connections.callSupport")}
                    </Button>
                    <Button size="small" variant="outlined" color="default" onClick={handleOpenTransferModal}>
                      Transferir Tickets
                    </Button>
                  </div>
                </div>
              </MainHeader>

              {statusImport?.all && (
                <Card style={{ margin: "0 8px 8px" }}>
                  <CardContent>
                    <Typography variant="subtitle2">
                      {statusImport?.this === -1 ? i18n.t("connections.buttons.preparing") : i18n.t("connections.buttons.importing")}
                    </Typography>
                    {statusImport?.this === -1
                      ? <CircularProgress size={24} />
                      : <CircularProgressWithLabel value={(statusImport.this / statusImport.all) * 100} />}
                  </CardContent>
                </Card>
              )}

              <Paper className={classes.mainPaper} variant="outlined">
                {whatsApps.length === 0 && !loading ? (
                  <div className={classes.emptyState}>
                    <WhatsApp style={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography variant="body2">Sin conexiones</Typography>
                  </div>
                ) : (
                  <div className={classes.mobileList}>
                    {whatsApps.map((whatsApp) => (
                      <div key={whatsApp.id} className={classes.card}>
                        {whatsApp.color && (
                          <div className={classes.colorBar} style={{ background: whatsApp.color }} />
                        )}
                        <div className={classes.cardTop}>
                          <div className={classes.cardIcon}>
                            {IconChannel(whatsApp.channel)}
                          </div>
                          <div className={classes.cardBody}>
                            <div className={classes.cardName}>{whatsApp.name}</div>
                            <div className={classes.cardNumber}>
                              {whatsApp.channel === "whatsapp" && whatsApp.number
                                ? formatSerializedId(whatsApp.number)
                                : whatsApp.channel === "whatsapp_oficial" && whatsApp.phone_number
                                  ? formatSerializedId(whatsApp.phone_number)
                                  : whatsApp.channel}
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            {renderStatusIcon(whatsApp)}
                            <Typography variant="caption" color="textSecondary">
                              {getStatusLabel(whatsApp.status)}
                            </Typography>
                            {whatsApp.isDefault && <CheckCircle style={{ color: green[500], fontSize: 16 }} />}
                          </div>
                        </div>
                        <div className={classes.cardBottom}>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            {renderActionButtons(whatsApp)}
                          </div>
                          <Can role={user.profile} perform="connections-page:addConnection"
                            yes={() => (
                              <div className={classes.cardActions}>
                                <IconButton size="small" onClick={() => handleEditWhatsApp(whatsApp)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleOpenPreDeleteModal(whatsApp.id)}>
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                                {whatsApp.channel === "whatsapp_oficial" && (
                                  <>
                                    <Tooltip title="Sincronizar templates">
                                      <IconButton size="small" onClick={() => handleSyncTemplates(whatsApp.id)}>
                                        <Sync fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Copiar webhook">
                                      <IconButton size="small" onClick={() => handleCopyWebhook(whatsApp.waba_webhook)}>
                                        <LinkOutlined fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </div>
                            )} />
                        </div>
                        <Typography variant="caption" color="textSecondary">
                          {format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
                        </Typography>
                      </div>
                    ))}
                    {loading && (
                      <Typography variant="caption" align="center" color="textSecondary" style={{ textAlign: "center" }}>
                        Cargando...
                      </Typography>
                    )}
                  </div>
                )}
              </Paper>

              {!HIDE_WHATSAPP_NEW_CONNECTION && <Can role={user.profile} perform="connections-page:addConnection"
                yes={() => (
                  <PopupState variant="popover" popupId="fab-new-connection">
                    {(popupState) => (
                      <>
                        <Fab color="primary" className={classes.fab} {...bindTrigger(popupState)}>
                          <Add />
                        </Fab>
                        <Menu {...bindMenu(popupState)}>
                          <MenuItem disabled={!planConfig?.plan?.useWhatsapp}
                            onClick={() => { handleOpenWhatsAppModal("whatsapp"); popupState.close(); }}>
                            <WhatsApp fontSize="small" style={{ marginRight: 10, color: "#25D366" }} /> WhatsApp
                          </MenuItem>
                          <MenuItem disabled={!planConfig?.plan?.useWhatsappOfficial}
                            onClick={() => { handleOpenWhatsAppModal("whatsapp_oficial"); popupState.close(); }}>
                            <WhatsApp fontSize="small" style={{ marginRight: 10, color: "#25D366" }} /> WhatsApp Oficial
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </PopupState>
                )} />}
            </>
          ) : (
            <>
              <MainHeader>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 12, flexWrap: "wrap" }}>
                  <Title>{i18n.t("connections.title")}</Title>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button variant="outlined" color="default" onClick={handleOpenTransferModal}>
                      Transferir Tickets
                    </Button>
                    <Button variant="outlined" color="primary" onClick={restartWhatsapps}>
                      {i18n.t("connections.restartConnections")}
                    </Button>
                    <Button variant="outlined" color="primary"
                      onClick={() => openInNewTab(`https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`)}>
                      {i18n.t("connections.callSupport")}
                    </Button>
                    {newConnectionMenu}
                  </div>
                </div>
              </MainHeader>

              {/* Stats bar */}
              <div className={classes.statsBar}>
                <div className={classes.statChip}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a" }} />
                  {whatsApps.filter(w => w.status === "CONNECTED").length} conectadas
                </div>
                <div className={classes.statChip}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc2626" }} />
                  {whatsApps.filter(w => w.status === "DISCONNECTED").length} desconectadas
                </div>
                <div className={classes.statChip}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#d97706" }} />
                  {whatsApps.filter(w => w.status === "qrcode").length} esperando QR
                </div>
                <div className={classes.statChip} style={{ marginLeft: "auto" }}>
                  Total: {whatsApps.length}
                </div>
              </div>

              {statusImport?.all && (
                <Card style={{ marginBottom: 16 }}>
                  <CardContent>
                    <Typography component="h5" variant="h5">
                      {statusImport?.this === -1 ? i18n.t("connections.buttons.preparing") : i18n.t("connections.buttons.importing")}
                    </Typography>
                    {statusImport?.this === -1
                      ? <CircularProgress size={24} />
                      : <>
                          <Typography component="h6" variant="h6" align="center">
                            {`${i18n.t("connections.typography.processed")} ${statusImport?.this} ${i18n.t("connections.typography.in")} ${statusImport?.all} ${i18n.t("connections.typography.date")}: ${statusImport?.date}`}
                          </Typography>
                          <Typography align="center">
                            <CircularProgressWithLabel style={{ margin: "auto" }} value={(statusImport.this / statusImport.all) * 100} />
                          </Typography>
                        </>}
                  </CardContent>
                </Card>
              )}

              <div className={classes.mainPaper}>
                {loading ? (
                  <div className={classes.desktopGrid}>
                    {[1, 2, 3].map(i => (
                      <div key={i} className={classes.desktopCard} style={{ height: 180, opacity: 0.4, background: "rgba(128,128,128,0.1)" }} />
                    ))}
                  </div>
                ) : whatsApps.length === 0 ? (
                  <div className={classes.emptyState}>
                    <WhatsApp style={{ fontSize: 64, opacity: 0.2 }} />
                    <Typography variant="h6" color="textSecondary">Sin conexiones</Typography>
                    <Typography variant="body2" color="textSecondary">Crea tu primera conexión con el botón "Nueva Conexión"</Typography>
                  </div>
                ) : (
                  <div className={classes.desktopGrid}>
                    {whatsApps.map((whatsApp) => {
                      const statusStyle = getStatusStyle(whatsApp.status);
                      return (
                        <div key={whatsApp.id} className={classes.desktopCard}>
                          {/* Color bar */}
                          <div
                            className={classes.desktopCardColorBar}
                            style={whatsApp.color ? { background: whatsApp.color } : undefined}
                          />

                          <div className={classes.desktopCardBody}>
                            {/* Header: icon + name + number */}
                            <div className={classes.desktopCardHeader}>
                              <div className={classes.desktopCardIcon}>
                                {IconChannel(whatsApp.channel)}
                              </div>
                              <div className={classes.desktopCardInfo}>
                                <div className={classes.desktopCardName}>
                                  {whatsApp.name}
                                  {whatsApp.isDefault && (
                                    <Tooltip title="Por defecto">
                                      <CheckCircle style={{ color: green[500], fontSize: 14, marginLeft: 6, verticalAlign: "middle" }} />
                                    </Tooltip>
                                  )}
                                </div>
                                <div className={classes.desktopCardNumber}>
                                  {getConnectionNumber(whatsApp)}
                                </div>
                              </div>
                            </div>

                            {/* Status chip + session actions */}
                            <div className={classes.desktopCardMeta}>
                              <Tooltip
                                title={
                                  whatsApp.status === "DISCONNECTED"
                                    ? i18n.t("connections.toolTips.disconnected.content")
                                    : whatsApp.status === "qrcode"
                                    ? i18n.t("connections.toolTips.qrcode.content")
                                    : whatsApp.status === "CONNECTED"
                                    ? i18n.t("connections.toolTips.connected.title")
                                    : i18n.t("connections.toolTips.timeout.content")
                                }
                              >
                                <span className={classes.statusChip} style={statusStyle}>
                                  {whatsApp.status === "OPENING"
                                    ? <CircularProgress size={10} style={{ color: statusStyle.color }} />
                                    : renderStatusIcon(whatsApp)}
                                  {getStatusLabel(whatsApp.status)}
                                </span>
                              </Tooltip>
                              <div style={{ marginLeft: "auto" }}>
                                {renderActionButtons(whatsApp)}
                              </div>
                            </div>
                          </div>

                          {/* Footer: date + edit/delete */}
                          <div className={classes.desktopCardFooter}>
                            <Typography className={classes.desktopCardDate}>
                              Actualizado: {format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
                            </Typography>
                            <Can role={user.profile} perform="connections-page:addConnection"
                              yes={() => (
                                <div className={classes.desktopCardActions}>
                                  <Tooltip title="Editar">
                                    <IconButton size="small" onClick={() => handleEditWhatsApp(whatsApp)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Eliminar">
                                    <IconButton size="small" onClick={() => handleOpenPreDeleteModal(whatsApp.id)}>
                                      <DeleteOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {whatsApp.channel === "whatsapp_oficial" && (
                                    <>
                                      <Tooltip title="Sincronizar templates">
                                        <IconButton size="small" onClick={() => handleSyncTemplates(whatsApp.id)}>
                                          <Sync fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Copiar webhook para Meta">
                                        <IconButton size="small" onClick={() => handleCopyWebhook(whatsApp.waba_webhook)}>
                                          <LinkOutlined fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </div>
                              )} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </MainContainer>
  );
};

export default Connections;
